/* eslint-disable no-case-declarations */
import {
  matchWildCardTopic,
  extractHTTPInfo,
  findDeviceId,
  findErrorCode,
  mqttInfoFormatted,
  mqttTopicFormatted,
  getBoolean,
  isStr,
  parseJSON,
  ERR_MQTT_DISCONNECTED,
  ERR_DEREGISTER_FAILED,
  ERR_SETTING_FAILED,
  ERR_OPERATION_FAILED,
  RESULT_CONFIG_FAILED,
  RESULT_PKGWARE_FAILED,
  RESULT_PKGWARE_UNKNOWN,
  RESULT_SETTING_UNKNOWN,
  RESULT_CONFIG_UPDATED,
  RESULT_SCHEDULE_UPDATED,
  RESULT_PKGWARE_UPDATED,
  STATE_CONFIG_START,
  STATE_CONFIG_END,
  STATE_PKGWARE_DOWNLOADING,
  STATE_PKGWARE_UNLOADED,
  STATE_PKGWARE_UPDATING,
  STATE_PKGWARE_SUCCESS,
  STATE_PKGWARE_FAILED,
  isModbusGateway,
  MAX_MQTT_SAMPLE,
} from '@/js/utils';
import Logger from '@/js/logger';
import {
  base64Url2Hex,
  convertHexaToInt,
  decodeModbusASCIIFrame,
  trimModbusASCIIFrame,
} from '@/js/dataConversion';
import MqttInstance from '@/js/MqttClient';
import Vue from 'vue';

const CONFIG_REQUEST_TIMEOUT = 15000;
const PKWARE_REQUEST_TIMEOUT = 120000;
const MODBUS_INTERVAL_TIMEOUT = 65000; // Interval + Relay Timeout
const HTTP_OK = 200;

const KEY_DEVICECFG_SIDE = 'device_config';
const KEY_DEVICEPKG_SIDE = 'device_pkgware';
const KEY_CLOUDCFG_SIDE = 'cloud_config';

const STATE_INITIAL = 'STATE_INITIAL';

// const STATE_PKGWARE_LATEST = 'UP_TO_DATE';

const STATE_SCHL_DISABLED = 'SCHEDULE_DISABLED';

const deviceTimeoutFunc = {};
const cloudTimeoutFunc = {};

const SET_CLIENT_INFO = (state, clientInfo) => {
  const info = mqttTopicFormatted(clientInfo);
  if (!info.stateTopic) {
    Logger.info('Skip client', info);
    return;
  }
  if (!state.clientList.find((el) => el.dev_id === info.dev_id)) {
    state.clientList.push(info);
  }
};

const REMOVE_MQTT_DEVICE = (state, devId) => {
  const deviceIndex = state.clientList.findIndex(
    (client) => client.dev_id === devId
  );
  const client = state.clientList[deviceIndex];
  const macAddr = client.mac_addr;
  clearTimeout(deviceTimeoutFunc[macAddr]);
  clearTimeout(cloudTimeoutFunc[macAddr]);
  clearConfigState(state, devId);
  clearResult(state, devId);

  state.mqttClient.unsubscribeTopic(client.stateTopic);
  state.mqttClient.unsubscribeTopic(client.readTopic);
  state.mqttClient.unsubscribeTopic(client.resDeviceTopic);
  if (client.mqtt2mbusTopic) {
    state.mqttClient.unsubscribeTopic(client.mqtt2mbusTopic);
    state.mqttClient.unsubscribeTopic(client.mbus2mqttTopic);
  }
  state.clientList.splice(deviceIndex, 1);
  Vue.delete(state.conn, macAddr);
};

const SET_DOWNLOAD_TOPIC = (state, topic) => {
  Logger.info('SET_DOWNLOAD_TOPIC', topic);

  if (!state.downloadTopic && topic) {
    state.downloadTopic = topic;
    state.mqttClient.subscribeTopic(topic);
  }
};

const SET_MQTT_DATA = (
  state,
  { destinationName, payloadString, portsState }
) => {
  let deviceIndex,
    macAddr,
    devID,
    res,
    currentState,
    status,
    body,
    dev_id,
    method,
    client_id,
    error_res;

  switch (true) {
    case state.downloadTopic &&
      matchWildCardTopic(destinationName, state.downloadTopic):
      res = parseJSON(payloadString);
      Logger.info('[MQTT Download Log]', destinationName, res);

      deviceIndex = state.clientList.findIndex(
        (client) => client.dev_client_id === res.dev_client_id
      );

      macAddr = state.clientList[deviceIndex].mac_addr;
      Vue.set(state.log, macAddr, res.download_url);
      break;
    case (deviceIndex = state.clientList.findIndex(
      (client) => client.stateTopic === destinationName
    )) !== -1:
      Logger.info('[MQTT Res]', destinationName, payloadString);

      macAddr = state.clientList[deviceIndex].mac_addr;
      Vue.set(state.conn, macAddr, getBoolean(payloadString));
      break;
    case (deviceIndex = state.clientList.findIndex(
      (client) => client.readTopic === destinationName
    )) !== -1:
      macAddr = state.clientList[deviceIndex].mac_addr;

      const isModbus = isModbusGateway(state.clientList[deviceIndex].dev_model);
      const currentTs = Date.now();

      if (!isModbus) {
        res = parseJSON(payloadString);
      } else {
        let payload;
        const decodedPayload = new TextDecoder().decode(payloadString);
        try {
          payload = parseJSON(decodedPayload);
        } catch (e) {}

        if (!Array.isArray(payload)) {
          payload = [decodedPayload];
        }

        payload.forEach((payloadStr) => {
          const raw_data = base64Url2Hex(payloadStr);
          const port_id_dec = convertHexaToInt(raw_data.substr(0, 8), 16);
          const port_id_key = port_id_dec.toString();
          const asciiFrame = raw_data.substring(8);
          const isAscii =
            asciiFrame.startsWith('3A') && asciiFrame.endsWith('0D0A');
          let encodedData = raw_data;
          if (isAscii) {
            encodedData =
              raw_data.substr(0, 8) +
              decodeModbusASCIIFrame(trimModbusASCIIFrame(asciiFrame));
          }
          const req_bcount = parseInt(encodedData.substr(12, 2), 16);
          const modbus_timeout =
            state.clientList[deviceIndex].modbus_timeout || 0;

          clearTimeout(deviceTimeoutFunc[port_id_key]);
          deviceTimeoutFunc[port_id_key] = setTimeout(() => {
            state.data[port_id_key].push({
              is_timeout: true,
            });
          }, MODBUS_INTERVAL_TIMEOUT + modbus_timeout);
          const rep_func = parseInt(encodedData.substr(10, 2), 16);
          // Skip handling modbus data for write command
          if (rep_func === 5) {
            return;
          }

          if (state.mqttLoading[macAddr]) {
            Vue.set(state.mqttLoading, macAddr, null);
          }

          res = {
            // slave_addr: parseInt(modbusData.substr(0, 2), 16),
            rep_func,
            // req_bcount,
            hex_data: encodedData.substr(14, req_bcount * 2),
            raw_data,
            ts: currentTs,
            isAscii,
            // port_id_dec,
            // req_addr:
          };

          if (!state.data[port_id_key]) {
            Vue.set(state.data, port_id_key, []);
          }

          state.data[port_id_key].push(res);
          const exceedingLength =
            state.data[port_id_key].length - MAX_MQTT_SAMPLE; /* .filter(
            (dta) => dta.port_id_dec === res.port_id_dec
            // dta.slave_addr === res.slave_addr && dta.req_func === res.rep_func
          ).length - MAX_SAMPLE; */
          if (exceedingLength > 0) {
            // let indexCount = 0;
            state.data[port_id_key].splice(0, exceedingLength);
            // Vue.set(
            //  state.data[macAddr],
            //  port_id_dec,
            //   state.data[macAddr][port_id_dec].splice(0, exceedingLength)
            /* state.data[macAddr].filter((dta) => {
              if (
                dta.port_id_dec === res.port_id_dec
                // dta.slave_addr === res.slave_addr &&
                // dta.req_func === res.rep_func &&
                //  dta.req_bcount === res.req_bcount
              ) {
                indexCount++;
                if (indexCount <= exceedingLength) return false;
                return true;
              }
              return true;
            }) */
            // );
          }
        });
        return;
      }

      if (!isStr(res)) {
        if (!state.data[macAddr]) {
          Vue.set(state.data, macAddr, []);
        }
        res.ts = currentTs;
        state.data[macAddr].push(res);
        const exceedingLength = state.data[macAddr].length - MAX_MQTT_SAMPLE;
        if (exceedingLength > 0) {
          state.data[macAddr].splice(0, exceedingLength);
        }
      }
      break;
    case (deviceIndex = state.clientList.findIndex(
      (client) => client.resDeviceTopic === destinationName
    )) !== -1:
      res = parseJSON(payloadString);
      Logger.info('[MQTT Res]', destinationName, payloadString);

      macAddr = state.clientList[deviceIndex].mac_addr;
      devID = state.clientList[deviceIndex].dev_id;
      switch (res.state) {
        case STATE_CONFIG_START:
          // clearTimeout(deviceTimeoutFunc[macAddr]);
          writeConfigState(state, {
            dev_id: devID,
            key: KEY_DEVICECFG_SIDE,
            value: STATE_CONFIG_START,
          });
          clearTimeout(deviceTimeoutFunc[macAddr]);
          deviceTimeoutFunc[macAddr] = setTimeout(() => {
            clearTimeout(cloudTimeoutFunc[macAddr]);
            clearConfigState(state, devID);
            updateResult(state, devID, RESULT_CONFIG_FAILED);
          }, CONFIG_REQUEST_TIMEOUT);
          break;
        case STATE_CONFIG_END:
          clearTimeout(deviceTimeoutFunc[macAddr]);
          currentState = readConfigState(state, {
            dev_id: devID,
            key: KEY_CLOUDCFG_SIDE,
          });
          writeConfigState(state, {
            dev_id: devID,
            key: KEY_DEVICECFG_SIDE,
            value: STATE_CONFIG_END,
          });
          if (currentState == null || currentState === STATE_CONFIG_END) {
            clearConfigState(state, devID);
            updateResult(state, devID, RESULT_CONFIG_UPDATED);
          }
          break;
        case STATE_SCHL_DISABLED:
          currentState = readConfigState(state, {
            dev_id: devID,
            key: KEY_DEVICECFG_SIDE,
          });
          if (currentState == null || currentState === STATE_CONFIG_END) {
            clearConfigState(state, devID);
            updateResult(state, devID, RESULT_SCHEDULE_UPDATED);
          }
          break;
        case STATE_PKGWARE_DOWNLOADING:
          Logger.info('STATE_PKGWARE_DOWNLOADING');

          writeConfigState(state, {
            dev_id: devID,
            key: KEY_DEVICEPKG_SIDE,
            value: STATE_PKGWARE_DOWNLOADING,
          });
          clearTimeout(deviceTimeoutFunc[macAddr]);
          deviceTimeoutFunc[macAddr] = setTimeout(() => {
            updateResult(state, devID, RESULT_PKGWARE_FAILED);
            clearConfigState(state, devID);
          }, PKWARE_REQUEST_TIMEOUT);
          // clearTimeout(deviceTimeoutFunc[macAddr]);
          break;
        case STATE_PKGWARE_UPDATING:
          Logger.info('STATE_PKGWARE_UPDATING');

          writeConfigState(state, {
            dev_id: devID,
            key: KEY_DEVICEPKG_SIDE,
            value: STATE_PKGWARE_UPDATING,
          });
          clearTimeout(deviceTimeoutFunc[macAddr]);
          deviceTimeoutFunc[macAddr] = setTimeout(() => {
            updateResult(state, devID, RESULT_PKGWARE_FAILED);
            clearConfigState(state, devID);
          }, PKWARE_REQUEST_TIMEOUT);
          break;
        case STATE_PKGWARE_UNLOADED:
          Logger.warn('RESULT_PKGWARE_FAILED');

          clearTimeout(deviceTimeoutFunc[macAddr]);
          updateResult(state, devID, RESULT_PKGWARE_FAILED);
          clearConfigState(state, devID);
          break;
        case STATE_PKGWARE_FAILED:
          Logger.warn('STATE_PKGWARE_FAILED');

          clearTimeout(deviceTimeoutFunc[macAddr]);
          updateResult(
            state,
            devID,
            RESULT_PKGWARE_UNKNOWN
          ); /* issue handle in next release */
          clearConfigState(state, devID);
          break;
        case STATE_PKGWARE_SUCCESS:
          Logger.info('STATE_PKGWARE_SUCCESS');

          clearTimeout(deviceTimeoutFunc[macAddr]);
          updateResult(state, devID, RESULT_PKGWARE_UPDATED);
          clearConfigState(state, devID);
          break;
        // case STATE_PKGWARE_LATEST:
        //     break;
        default:
          Logger.info({ res });
          throw new Error(res.error_code || res.state || ERR_OPERATION_FAILED);
      }
      break;
    case state.clientList.findIndex(
      (client) => client.responseTopic === destinationName
    ) !== -1:
      ({ status, body, dev_id, client_id, method } =
        extractHTTPInfo(payloadString));
      if (!dev_id) {
        dev_id = findDeviceId(body, portsState);
      }
      Logger.info(
        '[MQTT Res]',
        destinationName,
        'Raw data',
        payloadString,
        'status',
        status,
        'body',
        body,
        'dev_id',
        dev_id,
        'client_id',
        client_id,
        'method',
        method
      );

      devID = dev_id;
      if (
        (deviceIndex = state.clientList.findIndex(
          (client) => client.dev_id === devID
        )) === -1
      ) {
        return;
      }

      if (method !== 'DELETE') {
        macAddr = state.clientList[deviceIndex].mac_addr;
        clearTimeout(cloudTimeoutFunc[macAddr]);
        currentState = readConfigState(state, {
          dev_id: devID,
          key: KEY_DEVICECFG_SIDE,
        });
        writeConfigState(state, {
          dev_id: devID,
          key: KEY_CLOUDCFG_SIDE,
          value: STATE_CONFIG_END,
        });

        error_res = findErrorCode(body);
        Logger.warn('error_res', error_res);

        if (status === HTTP_OK && !error_res) {
          if (currentState == null || currentState === STATE_CONFIG_END) {
            updateResult(state, devID, RESULT_CONFIG_UPDATED);
            clearConfigState(state, devID);
          }
        } else if (state.wsClientID === client_id) {
          if (currentState == null || currentState === STATE_CONFIG_END) {
            clearTimeout(deviceTimeoutFunc[macAddr]);
            clearTimeout(cloudTimeoutFunc[macAddr]);
            updateResult(state, devID, RESULT_SETTING_UNKNOWN);
            clearConfigState(state, devID);

            /* updateResult(state, devID, RESULT_SETTING_UNKNOWN);
            clearConfigState(state, devID);
            if (error_res) {
              clearTimeout(deviceTimeoutFunc[macAddr]);
              clearTimeout(cloudTimeoutFunc[macAddr]);
              clearConfigState(state, devID);
              clearResult(state, devID);
            }
            */
            // clearConfigState(state, devID);
            // clearResult(state, devID);
          }
          throw new Error(ERR_SETTING_FAILED + ' - ' + error_res || status);
        }
      } else {
        if (status === HTTP_OK) {
          const client = state.clientList[deviceIndex];
          macAddr = client.mac_addr;
          clearTimeout(deviceTimeoutFunc[macAddr]);
          clearTimeout(cloudTimeoutFunc[macAddr]);
          clearConfigState(state, devID);
          clearResult(state, devID);

          state.mqttClient.unsubscribeTopic(client.stateTopic);
          state.mqttClient.unsubscribeTopic(client.readTopic);
          // state.mqttClient.unsubscribeTopic(client.responseTopic);
          state.mqttClient.unsubscribeTopic(client.resDeviceTopic);
          if (client.mqtt2mbusTopic) {
            state.mqttClient.unsubscribeTopic(client.mqtt2mbusTopic);
            state.mqttClient.unsubscribeTopic(client.mbus2mqttTopic);
          }
          state.clientList.splice(deviceIndex, 1);
          Vue.delete(state.conn, macAddr);
        } else if (state.wsClientID === client_id) {
          error_res = findErrorCode(body);
          throw new Error(ERR_DEREGISTER_FAILED + ' - ' + error_res || status);
        }
      }
      break;
    case state.clientList.findIndex(
      (client) => client.mqtt2mbusTopic === destinationName
    ) !== -1:
      Logger.info({ destinationName, payloadString });
      break;
    case (deviceIndex = state.clientList.findIndex(
      (client) => client.mbus2mqttTopic === destinationName
    )) !== -1:
      Logger.info({ destinationName, payloadString });

      res = parseJSON(payloadString);
      if (!isStr(res)) {
        macAddr = state.clientList[deviceIndex].mac_addr;
        if (!state.data[macAddr]) {
          Vue.set(state.data, macAddr, []);
        }

        if (res.slave_addr && res.rep_func) {
          res.hex_data = base64Url2Hex(res.rep_data);
          state.data[macAddr].push(res);
          const exceedingLength =
            state.data[macAddr].filter(
              (dta) =>
                dta.slave_addr === res.slave_addr &&
                dta.req_func === res.rep_func
            ).length - MAX_MQTT_SAMPLE;
          if (exceedingLength > 0) {
            let indexCount = 0;
            Vue.set(
              state.data,
              macAddr,
              state.data[macAddr].filter((dta) => {
                if (
                  dta.slave_addr === res.slave_addr &&
                  dta.req_func === res.rep_func
                ) {
                  indexCount++;
                  if (indexCount <= exceedingLength) return false;
                  return true;
                }
                return true;
              })
            );
          }
        }
        Logger.info('mbus2mqttData', JSON.stringify(state.data[macAddr]));
      }
      break;
  }
};

const REDUCE_SETTING_UPDATE = (state, dev_id) => {
  clearResult(state, dev_id);
};

const REDUCE_MQTT_DATA = (state, { uid, port_id }) => {
  if (state.data[uid]) {
    if (state.data[uid].length >= MAX_MQTT_SAMPLE - 3) {
      const reduceLength = MAX_MQTT_SAMPLE - 5;
      state.data[uid].splice(0, reduceLength);
    }
  } else if (port_id) {
    const port_id_key = port_id.toString();
    if (state.data[port_id_key]) {
      if (state.data[port_id_key].length >= MAX_MQTT_SAMPLE - 3) {
        const reduceLength = MAX_MQTT_SAMPLE - 5;
        state.data[port_id_key].splice(0, reduceLength);
      }
    }
  }
};

const SET_MQTT_LOADING = (state, payload) => {
  Vue.set(state.mqttLoading, payload.uid, payload.value);
};

const CLEAR_MQTT_DATA = (state, macAddr) => {
  // if (state.data[macAddr]) {
  Vue.set(state.data, macAddr, []);
  // Vue.set(state.data[macAddr], 'timestamp', []);
  // }
};

const CLEAR_DOWNLOAD_URL = (state, macAddr) => {
  Vue.set(state.log, macAddr, null);
};

const SET_MQTT_CLIENT = (state, params) => {
  // console.log(mqttInfoFormatted(params))
  const mqttParams = mqttInfoFormatted(params);
  state.mqttClient = new MqttInstance(mqttParams);
  state.wsClientID = mqttParams.webClientID;
};

const SET_CONN_STATE = (state, connState) => {
  state.connState = connState;
};

const SEND_FW_REQUEST = (
  state,
  { dev_id, latest, host_name, host_port, uri_path }
) => {
  if (!state.connState) throw new Error(ERR_MQTT_DISCONNECTED);
  const client = state.clientList.find((el) => el.dev_id === dev_id);
  if (!client) throw new Error(ERR_OPERATION_FAILED);
  const macAddr = client.mac_addr;
  const devID = client.dev_id;

  state.mqttClient.publishTopic(client.writeFirmwareTopic, {
    latest,
    host_name,
    host_port,
    uri_path,
  });
  Logger.info({
    writeFirmwareTopic: client.writeFirmwareTopic,
    latest,
    host_name,
    host_port,
    uri_path,
  });

  writeConfigState(state, {
    dev_id: devID,
    key: KEY_DEVICEPKG_SIDE,
    value: STATE_INITIAL,
  });

  clearTimeout(deviceTimeoutFunc[macAddr]);
  deviceTimeoutFunc[macAddr] = setTimeout(() => {
    updateResult(state, devID, RESULT_PKGWARE_FAILED);
    clearConfigState(state, devID);
  }, PKWARE_REQUEST_TIMEOUT);
};

const SEND_MQTT_CONFIG = (state, { dev_id, config_data }) => {
  if (!state.connState) throw new Error(ERR_MQTT_DISCONNECTED);
  const client = state.clientList.find((el) => el.dev_id === dev_id);
  if (!client) throw new Error(ERR_OPERATION_FAILED);
  const macAddr = client.mac_addr;
  const devID = client.dev_id;

  state.mqttClient.publishTopic(client.updateConfigTopic, config_data);
  writeConfigState(state, {
    dev_id: devID,
    key: KEY_DEVICECFG_SIDE,
    value: STATE_INITIAL,
  });

  clearTimeout(deviceTimeoutFunc[macAddr]);
  deviceTimeoutFunc[macAddr] = setTimeout(() => {
    clearTimeout(cloudTimeoutFunc[macAddr]);
    clearConfigState(state, devID);
    updateResult(state, devID, RESULT_CONFIG_FAILED);
  }, CONFIG_REQUEST_TIMEOUT);
};

const ACTIVATE_ALARM_STATE = (
  state,
  { mac_addr, port_name, port_id, event_id }
) => {
  if (
    !state.alarmState.find(
      (el) =>
        el.mac_addr === mac_addr &&
        (el.port_name === port_name || el.port_id === port_id) &&
        el.event_id === event_id
    )
  ) {
    state.alarmState.push({ mac_addr, port_name, port_id, event_id });
  }
};

const DEACTIVATE_ALARM_STATE = (state, params) => {
  const { mac_addr, port_name, port_id, event_id } = params || {};
  if (!mac_addr) state.alarmState = [];
  else if (
    state.alarmState.find(
      (el) =>
        el.mac_addr === mac_addr &&
        (port_name == null ||
          el.port_name === port_name ||
          el.port_id === port_id) &&
        (event_id == null || el.event_id === event_id)
    )
  ) {
    state.alarmState = state.alarmState.filter(
      (el) =>
        el.mac_addr !== mac_addr ||
        (port_name != null && el.port_name !== port_name) ||
        (port_id != null && el.port_id !== port_id) ||
        (event_id != null && el.event_id !== event_id)
    );
  }
};

function writeConfigState(state, { dev_id, key, value }) {
  const devIndex = state.configQueue.findIndex((el) => el.dev_id === dev_id);
  if (devIndex === -1) {
    const obj = { dev_id };
    obj[key] = value;
    state.configQueue.push(obj);
  } else {
    Vue.set(state.configQueue[devIndex], key, value);
  }
}

function clearConfigState(state, devID) {
  state.configQueue = state.configQueue.filter((dev) => dev.dev_id !== devID);
}

function readConfigState(state, { dev_id, key }) {
  const devIndex = state.configQueue.findIndex((el) => el.dev_id === dev_id);
  if (devIndex !== -1) return state.configQueue[devIndex][key];
}

function updateResult(state, dev_id, result) {
  const targetDev = state.updatedList.find(
    (device) => device.dev_id === dev_id
  );
  if (targetDev) {
    if (targetDev.result < result) Vue.set(targetDev, 'result', result);
  } else {
    state.updatedList.push({ dev_id, result });
  }
}

function clearResult(state, dev_id) {
  const targetIndex = state.updatedList.findIndex(
    (device) => device.dev_id === dev_id
  );
  if (targetIndex !== -1) state.updatedList.splice(targetIndex, 1);
}

export default {
  SET_CLIENT_INFO,
  SET_MQTT_CLIENT,
  REMOVE_MQTT_DEVICE,
  SET_MQTT_DATA,
  CLEAR_MQTT_DATA,
  REDUCE_MQTT_DATA,
  CLEAR_DOWNLOAD_URL,
  SET_CONN_STATE,
  ACTIVATE_ALARM_STATE,
  DEACTIVATE_ALARM_STATE,
  REDUCE_SETTING_UPDATE,
  SEND_MQTT_CONFIG,
  SEND_FW_REQUEST,
  SET_DOWNLOAD_TOPIC,
  SET_MQTT_LOADING,
};
